import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";

import { useLeaData } from "@/queries.ts";
import { getCurrentProgramYear } from "@/utils/dates";
import type { LeaDataModel } from "@/models";

export type QuickSearchIdType =
  | "CSPPID"
  | "CICN"
  | "SEID"
  | "Staff Local ID"
  | "Classroom ID"
  | "Local Family ID";

export type ResolvedQuickSearchRoute = {
  matched: boolean;
  to?: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
  notFoundMessage?: string;
};

function normalizeId(value: unknown): string {
  return typeof value === "string" ? value.trim().toUpperCase() : "";
}

export function resolveQuickSearchRoute(
  lea: LeaDataModel | undefined,
  leaid: string,
  searchType: QuickSearchIdType,
  rawTerm: string
): ResolvedQuickSearchRoute | undefined {
  const term = normalizeId(rawTerm);
  if (!term) return undefined;

  if (searchType === "Staff Local ID" && lea?.stff) {
    const match = lea.stff.find((s) => normalizeId(s.LocalStaffId) === term);

    if (match) {
      return {
        matched: true,
        to: "/$leaid/records/staff/$staffId",
        params: { leaid, staffId: term },
      };
    }

    return {
      matched: false,
      to: "/$leaid/records/staff",
      params: { leaid },
      search: { localStaffId: term },
      notFoundMessage: `No staff member found for Local ID: ${term}`,
    };
  }

  if (searchType === "SEID" && lea?.stff) {
    const match = lea.stff.find((s) => normalizeId(s.SEID) === term);

    if (match) {
      const staffIdParam = match.LocalStaffId || match.SEID || term;
      return {
        matched: true,
        to: "/$leaid/records/staff/$staffId",
        params: { leaid, staffId: staffIdParam },
      };
    }

    return {
      matched: false,
      to: "/$leaid/records/staff",
      params: { leaid },
      search: { seid: term },
      notFoundMessage: `No staff member found for SEID: ${term}`,
    };
  }

  if (searchType === "CSPPID" && lea?.chld) {
    const match = lea.chld.find((s) => normalizeId(s.CSPPID) === term);

    if (match) {
      return {
        matched: true,
        to: "/$leaid/records/child/$entityid",
        params: { leaid, entityid: term },
      };
    }

    return {
      matched: false,
      to: "/$leaid/records/child",
      params: { leaid },
      search: { CSPPID: term },
      notFoundMessage: `No child found for CSPPID: ${term}`,
    };
  }

  if (searchType === "CICN" && lea?.chld) {
    const match = lea.chld.find((s) => normalizeId(s.LocalChildId) === term);

    if (match) {
      const childIdParam = match.CSPPID || match.LocalChildId || term;
      return {
        matched: true,
        to: "/$leaid/records/child/$entityid",
        params: { leaid, entityid: childIdParam },
      };
    }

    return {
      matched: false,
      to: "/$leaid/records/child",
      params: { leaid },
      search: { CSPPID: term },
      notFoundMessage: `No child found for CSPPID: ${term}`,
    };
  }

  if (searchType === "Local Family ID" && lea?.fami) {
    const match = lea.fami.find((f) => normalizeId(f.LocalFamilyId) === term);

    if (match?.CSPPID) {
      return {
        matched: true,
        to: "/$leaid/records/child/$entityid",
        params: { leaid, entityid: match.CSPPID },
      };
    }

    return {
      matched: false,
      notFoundMessage: `No family found for Local Family ID: ${term}`,
    };
  }

  if (searchType === "Classroom ID" && lea?.entities?.classrooms) {
    const classrooms = lea.entities.classrooms;
    const currentProgramYear = getCurrentProgramYear();

    const matches = Object.entries(classrooms).filter(([, classroom]) => {
      return classroom.records?.some(
        (record) =>
          normalizeId(record?.LocalClassroomId) === term &&
          record?.ProgramYear === currentProgramYear
      );
    });

    if (matches.length === 1) {
      const entityId = matches[0][0];
      return {
        matched: true,
        to: "/$leaid/records/classroom/$entityid",
        params: { leaid, entityid: entityId },
      };
    }

    if (matches.length > 1) {
      return {
        matched: false,
        to: "/$leaid/records/classroom",
        params: { leaid },
        search: { classroomId: term },
      };
    }

    return {
      matched: false,
      to: "/$leaid/records/classroom",
      params: { leaid },
      notFoundMessage: `No classroom found for Classroom ID: ${term}`,
    };
  }

  return {
    matched: false,
    to: "/$leaid/records/quick-search",
    params: { leaid },
  };
}

export function useQuickSearchNavigate() {
  const navigate = useNavigate();
  const { leaid } = useParams({ from: "/$leaid" });
  const { data: lea } = useLeaData(leaid);

  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  const navigateToId = (searchType: QuickSearchIdType, rawTerm: string) => {
    if (!leaid) return;

    const resolved = resolveQuickSearchRoute(lea, leaid, searchType, rawTerm);
    if (!resolved) return;

    if (!resolved.matched && resolved.notFoundMessage) {
      setToastMsg(resolved.notFoundMessage);
      setShowToast(true);
    }

    if (resolved.to) {
      void navigate({
        to: resolved.to,
        params: resolved.params,
        search: resolved.search,
      } as Parameters<typeof navigate>[0]);
    }
  };

  return { navigateToId, showToast, setShowToast, toastMsg };
}
